/**
 * First-run teaching subtitle for starter-created tiles (S21): a tile added
 * from a template carries `source: { kind: 'template', templateId }` —
 * this resolves the template's `description` ("why a DBA watches this",
 * `templates/templateTypes.ts`) and shows it under the tile header until
 * dismissed. Dismissal persists per-tile via `dashboardStarterStore`
 * (`DashboardUiState.dismissedSubtitles`) so it survives restarts; a
 * non-template tile (or one whose templateId is unknown, e.g. imported from
 * a machine with a different catalog) renders nothing.
 */
import { useEffect } from 'react';
import { X } from 'lucide-react';
import { isQueryBackedTile, type DashboardTile } from '../../types/dashboardTypes';
import { findTemplateById } from '../../templates/templateCatalog';
import { useDashboardStarterStore } from '../../store/dashboardStarterStore';

export function TileTemplateSubtitle({
  dashboardId,
  tile,
}: {
  dashboardId: string;
  tile: DashboardTile;
}) {
  const ensureLoaded = useDashboardStarterStore((s) => s.ensureLoaded);
  const dismiss = useDashboardStarterStore((s) => s.dismiss);
  const loaded = useDashboardStarterStore((s) => s.loaded.has(dashboardId));
  const dismissed = useDashboardStarterStore(
    (s) => s.byDashboard[dashboardId]?.has(tile.id) === true,
  );

  useEffect(() => {
    void ensureLoaded(dashboardId);
  }, [dashboardId, ensureLoaded]);

  const templateId =
    isQueryBackedTile(tile) && tile.source.kind === 'template' ? tile.source.templateId : undefined;
  const template = templateId ? findTemplateById(templateId) : undefined;
  // Gate on `loaded` too — otherwise a template tile flashes its subtitle
  // for one frame before a persisted dismissal (from a prior session) loads
  // and hides it again (UI Quality Contract §4: no layout shift).
  if (!template || !loaded || dismissed) return null;

  return (
    <div
      className="flex items-start gap-1.5 border-b border-border/60 bg-muted/40 px-3 py-1.5 text-[11px] text-muted-foreground"
      data-testid="tile-starter-subtitle"
    >
      <span className="flex-1">{template.description}</span>
      <button
        onClick={() => void dismiss(dashboardId, tile.id)}
        className="shrink-0 rounded-sm p-0.5 hover:bg-muted hover:text-foreground"
        title="Dismiss this explanation"
        aria-label="Dismiss explanation"
        data-testid="tile-starter-subtitle-dismiss"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
